import React, { useState, useMemo, useEffect } from "react";
import "../barChart/barchart.css";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

const TOOLTIP_WIDTH = 110;
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;

  return (
    <div
      className="relative flex flex-col items-center"
      style={{ transform: "translateY(calc(-100% - 9px))" }}
    >
      <div
        className="text-white px-4 py-2 rounded-lg shadow-lg text-sm font-semibold text-center"
        style={{
          backgroundColor: "#089848",
          width: TOOLTIP_WIDTH,
          padding: "30px 0px",
        }}
      >
        ₹{payload[0].value.toLocaleString("en-IN")}
      </div>

      <div
        style={{
          width: 0,
          height: 0,
          borderLeft: "7px solid transparent",
          borderRight: "7px solid transparent",
          borderTop: "9px solid #089848",
          marginTop: "-1px",
        }}
      />
    </div>
  );
};

const BarChartComponent = ({ salesReport }) => {
  const data = useMemo(() => {
    return (
      salesReport?.data?.graph_sales?.outstanding_stats?.chart_data?.map(
        (item) => ({
          month: item.label,
          value: item.outstanding_amount,
          orders: item.outstanding_count,
        })
      ) || []
    );
  }, [salesReport]);
 

  const [activeBar, setActiveBar] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  useEffect(() => {
    if (!data.length) return;

    const currentMonth = new Date().toLocaleString("en-US", {
      month: "short",
    });
  

    const nextActive =
      data.find((item) => item.month === currentMonth) ||
      data[data.length - 1];

    setActiveBar((prev) =>
      prev?.month === nextActive.month ? prev : nextActive
    );
  }, [data]);


    const yAxisConfig = useMemo(() => {
  
      if (!data.length) {
        return {
          domain: [0, 100],
          ticks: [0, 20, 40, 60, 80, 100],
        };
      }
  
      const maxValue = Math.max(...data.map((d) => d.value));
      if (maxValue < 100) {
        return {
          domain: [0, 100],
          ticks: [0, 20, 40, 60, 80, 100],
        };
      }
      const paddedMax = maxValue * 1.1;
      const step = Math.ceil(paddedMax / 6 / 100) * 100;
      const safeStep = Math.max(step, 100);
      const domainMax = Math.ceil(paddedMax / safeStep) * safeStep;
  
      const ticks = Array.from({ length: 7 }, (_, i) => i * safeStep);
  
      return { domain: [0, domainMax], ticks };
    }, [data]);
    const formatYAxisTick = (v) => {
      if (v === 0) return "0";
      if (v < 1000) return v;                
      if (v < 10000) return `${(v / 1000).toFixed(1)}k`; 
      return `${Math.round(v / 1000)}k`;
    };

  if (!activeBar) return null;

  return (
    <div className="bg-white rounded-2xl p-6 max-w-md barchart-parent">
   
      <div className="mb-6">
        <p className="text-gray-400 text-sm">Total Outstanding Amount</p>
        <h2 className="text-3xl font-bold text-gray-900 mt-1">
          ₹{activeBar.value.toLocaleString("en-IN")}
        </h2>
        <p className="text-green-600 text-sm font-semibold mt-1">
          {activeBar.orders} Orders
        </p>
      </div>

      <hr className="mb-6 border-gray-100" />

      {/* Chart */}
      <div style={{ width: "100%", height: 390 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 20, right: 10, left: 0 ,bottom:10 }}>
            <CartesianGrid vertical={false} strokeDasharray="4 4" />

            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              dy={10}
            />

           <YAxis
               type="number"
               domain={yAxisConfig.domain}
               ticks={yAxisConfig.ticks}
               axisLine={false}
               tickLine={false}
               interval="preserveStartEnd"
               minTickGap={12}
               tickFormatter={formatYAxisTick}
             />

            <Tooltip
              content={<CustomTooltip />}
              position={tooltipPos}
              cursor={{ fill: "transparent" }}
              wrapperStyle={{ pointerEvents: "none" }}
            />

            <Bar
              dataKey="value"
              barSize={25}
              radius={[6, 6, 0, 0]}
              cursor="pointer"
              onMouseMove={(bar) => {
                if (!bar) return;
                setTooltipPos({
                  x: bar.x + bar.width / 2 - TOOLTIP_WIDTH / 2,
                  y: bar.y,
                });
              }}
              onClick={(barData) => setActiveBar(barData)}
            >
              {data.map((entry, index) => (
                <Cell
                  key={index}
                  fill={
                    entry.month === activeBar.month
                      ? "#16a34a"
                      : "#dcfce7"
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default BarChartComponent;
